import { useEffect, useRef, useState } from "react";
import { ImageIcon, Loader2, Upload } from "lucide-react";
import { catalogApi } from "../../api/catalogApi";
import { secondaryButtonClass } from "./schedulingControlStyles";

const errorMessage = error => error?.message || "The service image could not be saved. Please try again.";

export default function ServiceImagePanel({ catalogId, itemId, serviceName, canWrite, disabled, onBusyChange, onSaved }) {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const live = useRef(false);
  const inFlight = useRef(false);
  const fileInput = useRef(null);

  useEffect(() => {
    live.current = true;
    let active = true;
    setLoading(true); setError(""); setImage(null);
    catalogApi.listAllItemImages(catalogId, itemId)
      .then(images => { if (active) setImage(images.find(entry => entry.isPrimary) || images[0] || null); })
      .catch(failure => { if (active) setError(errorMessage(failure)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; live.current = false; };
  }, [catalogId, itemId, retry]);

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled || !canWrite || inFlight.current) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024 || !file.size) {
      setError("Choose a JPG, PNG or WebP image up to 5 MB.");
      return;
    }
    inFlight.current = true; setUploading(true); setError(""); onBusyChange(true);
    try {
      const saved = await catalogApi.uploadItemImage(catalogId, itemId, { file, altText: serviceName, isPrimary: true });
      if (live.current) { setImage(saved); onSaved("Service image saved."); }
    } catch (failure) {
      if (live.current) setError(errorMessage(failure));
    } finally {
      inFlight.current = false;
      if (live.current) { setUploading(false); onBusyChange(false); }
    }
  }

  return <div className="space-y-3 border-t border-slate-200 pt-4">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? <Loader2 size={20} className="animate-spin text-slate-400" aria-label="Loading service image" /> : image?.imageUrl ? <a href={image.imageUrl} target="_blank" rel="noreferrer" aria-label={`View full image for ${serviceName}`} className="h-full w-full"><img src={image.imageUrl} alt={image.altText || serviceName} className="h-full w-full object-contain" /></a> : <ImageIcon size={24} aria-hidden="true" className="text-slate-300" />}
      </div>
      <div className="min-w-0 space-y-2">
        <p className="text-sm font-semibold text-slate-700">Service image <span className="font-normal text-slate-400">(optional)</span></p>
        <p className="text-xs leading-relaxed text-slate-500">JPG, PNG or WebP, up to 5 MB. Uploads save immediately to this catalogue service.</p>
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Upload service image" className="sr-only" disabled={disabled || !canWrite || loading} onChange={upload} />
        {canWrite && <button type="button" disabled={disabled || loading} onClick={() => fileInput.current?.click()} className={secondaryButtonClass}>{uploading ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}{uploading ? "Uploading…" : image ? "Replace image" : "Upload image"}</button>}
      </div>
    </div>
    {error && <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-rose-700"><span>{error}</span><button type="button" disabled={disabled || uploading} onClick={() => setRetry(value => value + 1)} className="font-semibold underline">Reload image</button></div>}
  </div>;
}
